import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from 'redux/config/store'
import Box from 'core-system/Box/Box'
import MonthToggle from 'core-system/DatePicker/MonthToggle'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import styled from 'styled-components'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import {
  FlexAuthorization,
  FlexTransaction,
} from 'redux/employeePlatform/employeePlatformTypes'
import ProgramPills from 'shared/components/ProgramPills'
import { formatCurrency } from 'shared/CurrencyFunctions'
import { CurrencyType } from 'features/Programs/Shared/types'
import Table, { TableRow, TableRowCell } from 'core-system/Table'

const Container = styled.div`
  padding: 2rem;
`

const TransactionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 2rem;
  align-items: start;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`

const MonthSelectorCol = styled.div`
  grid-column: 1 / span 4;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-column: 1 / -1;
  }
`

const TransactionsCol = styled.div`
  grid-column: 5 / -1;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-column: 1 / -1;
  }
`

const MonthSelectorBox = styled(Box)`
  padding: 1rem;
  margin-bottom: 1rem;
`

const MetricRow = styled(FlexContainer)`
  justify-content: space-between;
  margin-top: 0.75rem;
`

const SectionHeading = styled(Text)`
  margin-bottom: 0.5rem;
`

const TableSection = styled.div`
  margin-bottom: 1.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`

function getActivityItemDescription(
  item: FlexAuthorization | FlexTransaction
): string {
  const name =
    (item as any).name ??
    (item as any).description ??
    (item as any).merchantName ??
    (item as any).merchant_name
  return name && String(name).trim() ? String(name) : '—'
}

const EmployeeTransactionsView = React.memo(() => {
  const dispatch = useDispatch()
  const { profileData, monthActivity } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const conversionRates = useSelector(
    (state: AppState) => state.application.properties
  )

  const currency: CurrencyType =
    (profileData?.worksiteCurrency as CurrencyType) || 'USD'

  const [currentDate, setCurrentDate] = useState(moment().format('YYYY-MM'))

  const authorizations = [...(monthActivity?.authorizations ?? [])].sort(
    (a, b) => moment(b.created).valueOf() - moment(a.created).valueOf()
  )
  const transactions = [...(monthActivity?.transactions ?? [])].sort(
    (a, b) => moment(b.created).valueOf() - moment(a.created).valueOf()
  )

  const fetchForMonth = useCallback(
    (month: string) => {
      dispatch(employeePlatformActions.getMonthTransactions({ month }))
    },
    [dispatch]
  )

  useEffect(() => {
    fetchForMonth(currentDate)
  }, [currentDate, fetchForMonth])

  const handleDateChange = useCallback((isIncrease: boolean) => {
    setCurrentDate((prev) => {
      const m = moment(prev, 'YYYY-MM')
      const next = isIncrease ? m.add(1, 'month') : m.subtract(1, 'month')
      return next.format('YYYY-MM')
    })
  }, [])

  return (
    <Container>
      <TransactionsGrid>
        <MonthSelectorCol>
          <MonthSelectorBox>
            <MonthToggle
              currentDate={currentDate}
              onIncreaseClick={() => handleDateChange(true)}
              onDecreaseClick={() => handleDateChange(false)}
            />
            <MetricRow marginTop='1.5rem'>
              <Text variant='action4'>Pending</Text>
              <Text variant='action4'>{authorizations.length}</Text>
            </MetricRow>
            <MetricRow>
              <Text variant='action4'>Posted</Text>
              <Text variant='action4'>{transactions.length}</Text>
            </MetricRow>
          </MonthSelectorBox>
        </MonthSelectorCol>
        <TransactionsCol>
          <TableSection>
            <SectionHeading variant='action1' fontWeight={600}>
              Pending
            </SectionHeading>
            <Table
              headerCells={[
                <TableRowCell key='date'>Date</TableRowCell>,
                <TableRowCell key='desc'>Description</TableRowCell>,
                <TableRowCell key='programs'>Programs</TableRowCell>,
                <TableRowCell key='amount' style={{ textAlign: 'right' }}>
                  Amount
                </TableRowCell>,
              ]}
              rows={authorizations.map((auth, idx) => (
                <TableRow
                  key={
                    (auth as any).id ??
                    (auth as any).internalId ??
                    `pending-${idx}`
                  }
                >
                  <TableRowCell>
                    <Text variant='action4' textColor={palette.text.secondary}>
                      {moment(auth.created).format('MMM D, YYYY')}
                    </Text>
                  </TableRowCell>
                  <TableRowCell>
                    <Text variant='action2'>
                      {getActivityItemDescription(auth)}
                    </Text>
                  </TableRowCell>
                  <TableRowCell>
                    <ProgramPills
                      programAmounts={auth.programAmounts}
                      marginTop={0}
                    />
                  </TableRowCell>
                  <TableRowCell style={{ textAlign: 'right' }}>
                    <Text variant='action2' fontWeight={600}>
                      {formatCurrency(currency, auth.amount, conversionRates)}
                    </Text>
                  </TableRowCell>
                </TableRow>
              ))}
              showPagination={false}
              maxRows={100}
              emptyMessage='No pending transactions'
            />
          </TableSection>

          <TableSection>
            <SectionHeading variant='action1' fontWeight={600}>
              Posted
            </SectionHeading>
            <Table
              headerCells={[
                <TableRowCell key='date'>Date</TableRowCell>,
                <TableRowCell key='desc'>Description</TableRowCell>,
                <TableRowCell key='programs'>Programs</TableRowCell>,
                <TableRowCell key='amount' style={{ textAlign: 'right' }}>
                  Amount
                </TableRowCell>,
              ]}
              rows={transactions.map((tx, idx) => (
                <TableRow
                  key={
                    (tx as any).id ?? (tx as any).internalId ?? `posted-${idx}`
                  }
                >
                  <TableRowCell>
                    <Text variant='action4' textColor={palette.text.secondary}>
                      {moment(tx.created).format('MMM D, YYYY')}
                    </Text>
                  </TableRowCell>
                  <TableRowCell>
                    <Text variant='action2'>
                      {getActivityItemDescription(tx)}
                    </Text>
                  </TableRowCell>
                  <TableRowCell>
                    <ProgramPills
                      programAmounts={tx.programAmounts}
                      marginTop={0}
                    />
                  </TableRowCell>
                  <TableRowCell style={{ textAlign: 'right' }}>
                    <Text variant='action2' fontWeight={600}>
                      {formatCurrency(currency, tx.amount, conversionRates)}
                    </Text>
                  </TableRowCell>
                </TableRow>
              ))}
              showPagination={false}
              maxRows={100}
              emptyMessage='No posted transactions'
            />
          </TableSection>
        </TransactionsCol>
      </TransactionsGrid>
    </Container>
  )
})

EmployeeTransactionsView.displayName = 'EmployeeTransactionsView'

export default EmployeeTransactionsView
